import { useCallback, useState } from 'react';
import { Modal, Pressable } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';

// --- Styled Components ---

const Overlay = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
`;

const DialogCard = styled.View`
  width: 90%;
  max-width: 360px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 16px;
  padding: 24px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  align-items: center;
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 18px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 8px;
  text-align: center;
`;

const Description = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  line-height: 22px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 24px;
  text-align: center;
`;

const ButtonGroup = styled.View`
  width: 100%;
  gap: 10px;
`;

const PrimaryButton = styled(Pressable)`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  background-color: ${({ $variant }) =>
    $variant === 'success'
      ? 'rgba(58, 232, 105, 0.15)'
      : 'rgba(255, 255, 255, 0.08)'};
  border-width: 1px;
  border-color: ${({ $variant }) =>
    $variant === 'success'
      ? REDESIGN_COLORS.scoreGood
      : REDESIGN_COLORS.borderSubtle};
`;

const PrimaryButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 15px;
  color: ${({ $variant }) =>
    $variant === 'success'
      ? REDESIGN_COLORS.scoreGood
      : REDESIGN_COLORS.textPrimary};
`;

const CancelButton = styled(Pressable)`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
`;

const CancelButtonText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

/**
 * EndNegotiationDialog - Confirmation modal for closing a negotiation.
 *
 * Outcomes:
 * - 'deal_reached'   - User accepted the terms / bought this car.
 * - 'abandoned'      - User chose to walk away (assisted: triggers courtesy follow-up).
 * - 'closed'         - User bought a different car.
 * - 'sold_to_other'  - Dealer sold the car to another buyer (we lost the listing).
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether the modal is shown
 * @param {Function} props.onClose - Called to dismiss without action
 * @param {Function} props.onConfirm - Called with one of the outcomes above
 * @param {boolean} props.isAssisted - True when negotiation.autonomy === 'assisted'
 */
const EndNegotiationDialog = ({ visible, onClose, onConfirm, isAssisted }) => {
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Overlay>
        <DialogCard>
          <Title>End this negotiation?</Title>
          <Description>
            {isAssisted
              ? 'A courtesy message will be sent to the dealer when relevant.'
              : 'This negotiation will be marked as closed.'}
          </Description>

          <ButtonGroup>
            <PrimaryButton $variant="success" onPress={() => onConfirm?.('deal_reached')}>
              <PrimaryButtonText $variant="success">
                I'm ok with the terms
              </PrimaryButtonText>
            </PrimaryButton>

            <PrimaryButton $variant="default" onPress={() => onConfirm?.('abandoned')}>
              <PrimaryButtonText $variant="default">
                I decided not to proceed
              </PrimaryButtonText>
            </PrimaryButton>

            <PrimaryButton $variant="default" onPress={() => onConfirm?.('closed')}>
              <PrimaryButtonText $variant="default">
                I bought a different car
              </PrimaryButtonText>
            </PrimaryButton>

            <PrimaryButton $variant="default" onPress={() => onConfirm?.('sold_to_other')}>
              <PrimaryButtonText $variant="default">
                The dealer sold it to another buyer
              </PrimaryButtonText>
            </PrimaryButton>
          </ButtonGroup>

          <CancelButton onPress={onClose}>
            <CancelButtonText>Keep negotiating</CancelButtonText>
          </CancelButton>
        </DialogCard>
      </Overlay>
    </Modal>
  );
};

export default EndNegotiationDialog;
